import {
  createContext,
  useContext,
  useMemo,
  useSyncExternalStore,
} from "react";
import type { PreOrderFlow } from "@/app/api/types/preOrder";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Action that produced the last error — used by retry to fire the right path. */
export type ModifyAction = "flushSave";

export interface ModifyBufferSlice {
  /** flowId → patch of changed fields */
  edits: Record<string, Partial<PreOrderFlow>>;
  /** locally-added flows (not yet persisted) */
  added: PreOrderFlow[];
  /** ids of original flows the user marked for deletion */
  deleted: Set<string>;
  isModifying: boolean;
  lastError: { message: string; at: number; action: ModifyAction } | null;
}

const EMPTY_SLICE: ModifyBufferSlice = Object.freeze({
  edits: {},
  added: [],
  deleted: new Set<string>(),
  isModifying: false,
  lastError: null,
}) as ModifyBufferSlice;

function isEmptySlice(s: ModifyBufferSlice): boolean {
  return (
    !s.isModifying &&
    s.lastError === null &&
    s.added.length === 0 &&
    s.deleted.size === 0 &&
    Object.keys(s.edits).length === 0
  );
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export function createModifyBufferStore() {
  const slices = new Map<string, ModifyBufferSlice>();
  const listeners = new Set<() => void>();

  const emit = () => {
    for (const l of listeners) l();
  };

  const getSlice = (preOrderId: string): ModifyBufferSlice => {
    return slices.get(preOrderId) ?? EMPTY_SLICE;
  };

  const writeSlice = (
    preOrderId: string,
    updater: (prev: ModifyBufferSlice) => ModifyBufferSlice,
  ) => {
    const prev = slices.get(preOrderId) ?? EMPTY_SLICE;
    const next = updater(prev);
    if (next === prev) return;
    if (isEmptySlice(next)) {
      slices.delete(preOrderId);
    } else {
      slices.set(preOrderId, next);
    }
    emit();
  };

  return {
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSlice,

    /** True when the supplied pre-order is currently in modify mode and
     * has at least one buffered change (edits/adds/deletes). Used by the
     * focus page's unsaved-changes guards so they react only to the order
     * the user is actually looking at. */
    isPreOrderDirty(preOrderId: string): boolean {
      const slice = slices.get(preOrderId);
      if (!slice?.isModifying) return false;
      return (
        slice.added.length > 0 ||
        slice.deleted.size > 0 ||
        Object.keys(slice.edits).length > 0
      );
    },

    /** Sum of buffered changes (edits + adds + deletes) across the supplied
     * pre-orders, counting only those currently in modify mode. Used by the
     * partner-group "unsaved changes" badge to surface how many uncommitted
     * edits the user has pending across an entire group at once. */
    countChangesForPreOrders(preOrderIds: readonly string[]): number {
      let n = 0;
      for (const id of preOrderIds) {
        const slice = slices.get(id);
        if (!slice?.isModifying) continue;
        // Edits that were subsequently deleted are counted once under
        // `deleted.size` — avoids double-counting the same flow row.
        const editedNotDeleted = Object.keys(slice.edits).filter(
          (flowId) => !slice.deleted.has(flowId),
        ).length;
        n += editedNotDeleted + slice.added.length + slice.deleted.size;
      }
      return n;
    },

    /**
     * Single entry point for starting a modify session on a pre-order.
     * Force-exits modify mode on any *other* PO before running `action`,
     * enforcing the invariant that at most one PO is ever in modify mode
     * at a time. The focused-PO `UnsavedNavigationDialog` guards against
     * losing unsaved work on navigation, so a leftover modify slice from
     * a different page can be safely discarded here.
     */
    requestEnterModify(requesterId: string, action: () => void): void {
      const own = slices.get(requesterId);
      if (own?.isModifying) {
        action();
        return;
      }
      for (const [id, slice] of slices) {
        if (id === requesterId) continue;
        if (slice.isModifying) {
          writeSlice(id, () => EMPTY_SLICE);
        }
      }
      action();
    },

    /** Enter modify mode and reset the buffer for this pre-order. */
    enterModify(preOrderId: string) {
      writeSlice(preOrderId, () => ({
        edits: {},
        added: [],
        deleted: new Set<string>(),
        isModifying: true,
        lastError: null,
      }));
    },

    /** Cancel modify mode and discard all buffered changes. */
    cancelModify(preOrderId: string) {
      writeSlice(preOrderId, () => EMPTY_SLICE);
    },

    /** Clear the buffer after a successful flush (also exits modify mode). */
    clearBuffer(preOrderId: string) {
      writeSlice(preOrderId, () => EMPTY_SLICE);
    },

    /** Drop buffered edits/adds/deletes without exiting modify mode. Used
     * mid-flow (e.g. between local persist and status transition) so that a
     * retry doesn't re-send already-persisted additions, while keeping the
     * row in modify mode so concurrent cell edits are still captured in the
     * buffer (and not silently committed via the live fallback). */
    drainBuffer(preOrderId: string) {
      writeSlice(preOrderId, (s) => ({
        ...s,
        edits: {},
        added: [],
        deleted: new Set<string>(),
      }));
    },

    /** Force-exit modify mode (e.g. status moved out of PENDING/CONFIRMED). */
    forceExitModify(preOrderId: string) {
      writeSlice(preOrderId, () => EMPTY_SLICE);
    },

    setLastError(
      preOrderId: string,
      error: { message: string; at: number; action: ModifyAction } | null,
    ) {
      writeSlice(preOrderId, (s) => ({ ...s, lastError: error }));
    },

    /** Patch an existing original flow with edits. */
    patchOriginalFlow(
      preOrderId: string,
      flowId: string,
      updates: Partial<PreOrderFlow>,
    ) {
      writeSlice(preOrderId, (s) => ({
        ...s,
        edits: {
          ...s.edits,
          [flowId]: { ...s.edits[flowId], ...updates },
        },
      }));
    },

    /** Patch a locally-added flow in place. */
    patchAddedFlow(
      preOrderId: string,
      flowId: string,
      updates: Partial<PreOrderFlow>,
    ) {
      writeSlice(preOrderId, (s) => {
        const idx = s.added.findIndex((f) => f.id === flowId);
        if (idx < 0) return s;
        const nextAdded = [...s.added];
        nextAdded[idx] = { ...nextAdded[idx], ...updates } as PreOrderFlow;
        return { ...s, added: nextAdded };
      });
    },

    /** Append a newly-created flow to the buffer. */
    addFlow(preOrderId: string, flow: PreOrderFlow) {
      writeSlice(preOrderId, (s) => ({ ...s, added: [...s.added, flow] }));
    },

    /** Mark an original flow for deletion. */
    markDeleted(preOrderId: string, flowId: string) {
      writeSlice(preOrderId, (s) => {
        const next = new Set(s.deleted);
        next.add(flowId);
        return { ...s, deleted: next };
      });
    },

    /** Remove a locally-added flow (the user deleted a row they just created). */
    removeAddedFlow(preOrderId: string, flowId: string) {
      writeSlice(preOrderId, (s) => ({
        ...s,
        added: s.added.filter((f) => f.id !== flowId),
      }));
    },
  };
}

export type ModifyBufferStore = ReturnType<typeof createModifyBufferStore>;

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export const ModifyBufferStoreContext = createContext<ModifyBufferStore | null>(
  null,
);

function useStore(): ModifyBufferStore {
  const store = useContext(ModifyBufferStoreContext);
  if (!store) {
    throw new Error(
      "useModifyBufferStore must be used within ModifyBufferStoreContext",
    );
  }
  return store;
}

/** Returns the imperative store handle (setters, snapshot reads in effects). */
export function useModifyBufferStore(): ModifyBufferStore {
  return useStore();
}

/** Optional variant: returns null when no store provider is mounted (e.g.
 * surfaces that can render without modify-mode infrastructure). */
export function useModifyBufferStoreOptional(): ModifyBufferStore | null {
  return useContext(ModifyBufferStoreContext);
}

/**
 * Subscribes to the buffer slice for a single pre-order. Components only
 * re-render when *their* slice changes — edits to other pre-orders do not
 * cascade.
 */
export function useModifyBufferSlice(preOrderId: string): ModifyBufferSlice {
  const store = useStore();
  return useSyncExternalStore(store.subscribe, () =>
    store.getSlice(preOrderId),
  );
}

/** True when the supplied pre-order is currently in modify mode with at
 * least one buffered change. Re-renders only when that flips. */
export function useIsPreOrderDirty(preOrderId: string): boolean {
  const store = useModifyBufferStoreOptional();
  return useSyncExternalStore(
    store ? store.subscribe : (_l: () => void) => () => {},
    () => (store ? store.isPreOrderDirty(preOrderId) : false),
    () => false,
  );
}

/** True when any of the supplied pre-orders is currently in modify mode. */
export function useAnyModifying(preOrderIds: readonly string[]): boolean {
  const store = useModifyBufferStoreOptional();
  const idsKey = preOrderIds.join("|");
  const subscribe = useMemo(
    () => (store ? store.subscribe : (_l: () => void) => () => {}),
    [store],
  );
  const getSnapshot = useMemo(
    () => () => {
      if (!store) return false;
      for (const id of preOrderIds) {
        if (store.getSlice(id).isModifying) return true;
      }
      return false;
    },
    // idsKey captures membership of preOrderIds; safe to ignore array identity
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [store, idsKey],
  );
  return useSyncExternalStore(subscribe, getSnapshot);
}

/** Sum of buffered changes across the supplied pre-orders. Re-renders only
 * when the aggregated count changes. */
export function useChangesForPreOrders(preOrderIds: readonly string[]): number {
  const store = useModifyBufferStoreOptional();
  const idsKey = preOrderIds.join("|");
  const subscribe = useMemo(
    () => (store ? store.subscribe : (_l: () => void) => () => {}),
    [store],
  );
  const getSnapshot = useMemo(
    () => () => (store ? store.countChangesForPreOrders(preOrderIds) : 0),
    // idsKey captures membership of preOrderIds; safe to ignore array identity
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [store, idsKey],
  );
  return useSyncExternalStore(subscribe, getSnapshot, () => 0);
}
